import { Paragraphs } from "types/Paragraph";
import { useWordDocumentGenerator } from "~/composables/wordGenerator/useWordDocumentGenerator";
import { usePDFGenerator } from "./pdfGenerator/usePDFGenerator";

import { ExportDocumentFormat } from "types/enums/ExportDocumentFormat.enum";
import { useClipboardHTMLGenerator } from "./clipboardGenerator/useClipboardHTMLGenerator";

export const useExportLoadedDocument = async (
  documentId: string,
  format: ExportDocumentFormat = ExportDocumentFormat.docx,
  returnToStudent: boolean = false,
  printMode: boolean = false,
  hideScores: boolean = false,
  fileNameOverride: string | undefined = undefined
) => {
  const documentStore = useSubmittedDocumentStore(documentId);
  const { revision, submittedDocument } = storeToRefs(documentStore);

  const checklistStore = useDocumentChecklistStore(documentId);
  const {
    useChecklist,
    useTotalEarnedScore,
    useTotalScore,
    useAllChecklistCriteria,
  } = storeToRefs(checklistStore);

  const paragraphStore = useDocumentRevisionParagraphsStore(documentId);
  const { wordChecklistCells } = storeToRefs(paragraphStore);

  const annotationStore = useDocumentAnnotationsStore(documentId);
  const { allAnnotations: annotations } = storeToRefs(annotationStore);

  const appUserData = useAppUserDataStore();
  const { commentColor } = storeToRefs(appUserData);

  if (!submittedDocument.value || !revision.value) {
    return;
  }

  const paragraphs = [] as Paragraphs;

  for (var paragraph of useDocumentParagraphsStore(documentId).paragraphs) {
    const paragraphStore = useDocumentParagraphStore(documentId, paragraph.id);

    if (paragraphStore.paragraph) {
      paragraphs.push(paragraphStore.paragraph);
    }
  }

  let blob: Blob | undefined = undefined;

  if (format == ExportDocumentFormat.docx) {
    const { exportWordDocument } = useWordDocumentGenerator();

    blob = await exportWordDocument(
      submittedDocument.value!,
      revision.value!,
      useChecklist.value!,
      useAllChecklistCriteria.value!,
      wordChecklistCells.value,
      paragraphs,
      annotations.value,
      useTotalEarnedScore.value,
      useTotalScore.value,
      submittedDocument.value?.inkColor ?? commentColor.value,
      returnToStudent,
      hideScores,
      fileNameOverride
    );
  }

  if (format == ExportDocumentFormat.pdf) {
    const { exportPDFDocument } = usePDFGenerator();

    blob = await exportPDFDocument(
      submittedDocument.value!,
      revision.value!,
      useChecklist.value!,
      useAllChecklistCriteria.value!,
      wordChecklistCells.value,
      paragraphs,
      annotations.value,
      useTotalEarnedScore.value,
      useTotalScore.value,
      commentColor.value,
      returnToStudent,
      printMode,
      hideScores,
      fileNameOverride
    );
  }

  if (format == ExportDocumentFormat.clipboard) {
    const html = await useClipboardHTMLGenerator(
      submittedDocument.value!,
      revision.value!,
      useChecklist.value!,
      useAllChecklistCriteria.value!,
      wordChecklistCells.value,
      paragraphs,
      annotations.value,
      useTotalEarnedScore.value,
      useTotalScore.value,
      commentColor.value
    );

    const htmlContent = html.innerHTML;

    // Generate plain text content from the clone
    const textContent = html.innerText || html.textContent || "";

    const blobHtml = new Blob([htmlContent], { type: "text/html" });
    const blobText = new Blob([textContent], { type: "text/plain" });

    // Create a ClipboardItem with both formats
    const data = [
      new ClipboardItem({
        [blobHtml.type]: blobHtml,
        [blobText.type]: blobText,
      }),
    ];

    // const documentContainer = document.getElementById("paragraphs-container");

    // if (!documentContainer) {
    //   throw new Error("Document container not found");
    // }

    // // Prepend the docuemnt container with the html
    // documentContainer.innerHTML = htmlContent + documentContainer.innerHTML;

    navigator.clipboard.write(data);

    useBaseToast("Copied to clipboard", "success");
  }

  if (returnToStudent && blob) {
    return blob;
  }
};
